import React, { useEffect, useRef, useState } from "react"
import { Button, Card, Collapse, Stack, Table } from "react-bootstrap"
import { Link } from "react-router-dom"
import PageTitleButton from "../../components/PageTitleButton"
import SearchBox from "../../components/SearchBox"
import SweetAlerts from "../../components/SweetAlerts"
import TooltipComponent from "../../components/Tooltip"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../lib/GlobalMethods"
import ChargingStationListSkeleton from "../../skeleton/chargingStationManagement/index"
import InfiniteScroll from "react-infinite-scroll-component"
import recordNotFound from "../../assets/images/record_not_found.png"
import useDebounce from "../../lib/hooks/useDebounce"
import AddEditChargingStation from "./AddEditChargingStation"
import { toast } from "react-toastify"
import { modulePermission } from "../../lib/GlobalVariables"
import { QRCode } from "react-qrcode-logo"
import ModalComponent from "../../components/Modal"
import { useSelector } from "react-redux"

const ChargingStations = () => {
  const canEdit = HasModulePermission(modulePermission.station, "edit")
  const canDelete = HasModulePermission(modulePermission.station, "delete")
  const canAdd = HasModulePermission(modulePermission.station, "add")
  const { userDetails } = useSelector((state) => state.basicInformation)

  const [data, setData] = useState(null)
  const [show, setShow] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [qrModel, setQrModel] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [filterObj, setFilterObj] = useState({
    brand_id: false,
    connector_id: false,
    vehicle_type: false,
    archived: false,
    active: false,
  })
  const qrCanvasRef = useRef(null)

  const [archived, setArchived] = useState(true)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleShow = () => setShow(true)

  const handleShowAlert = () => setShowAlert(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setIsDelete(false)
    setShowAlert(false)
  }

  const { handleAccess } = useHandleUnauthorizedAccess()

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.chargingStationList}?page=${
          pagination.current_page
        }&search_term=${searchQuery ? searchQuery : ""}&archived=${
          filterObj.archived
        }&active=${filterObj.active}&limit=15`,
      })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      handleAccess({ code: response?.data?.code })

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData],
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const handleArchive = async (id) => {
    try {
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: archived
          ? `${admin.restoreChargingStation + id}`
          : `${admin.deleteChargingStation + id}`,
      })

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message)

        setData((data) => {
          if (filterObj.archived === false && filterObj.active === false) {
            return data.map((station) => {
              if (station.id === id) {
                if (!archived) {
                  station.deletedAt = Date.now()
                  station.chargers = []
                } else {
                  station.deletedAt = null
                }
              }
              return station
            })
          }
        })

        setRecordsCount((prev) => ({
          ...prev,
          active_records: prev.active_records - 1,
          archived_records: prev.archived_records + 1,
        }))

        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response.data.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page])

  const filterOnClick = () => {
    setOpen(!open)
  }

  const closeQrModel = () => {
    setQrModel(false)
  }

  const downloadQRCode = async () => {
    if (!qrCanvasRef.current) return
    try {
      const fileType = "image/png"
      const fileName = `QR Code ${qrCanvasRef.current?.props?.value?.split("=")[1]}.png`
      qrCanvasRef.current?.download(fileType, fileName)
    } catch (err) {
      err.message && toast.error(err.message || "Failed to download QR Code")
    }
  }

  return (
    <>
      {canAdd && userDetails?.dataScope !== "OWN" ? (
        <PageTitleButton
          PagetitleText={"Charging Stations"}
          HeaderButtonText={"Add Stations"}
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Charging Stations"}
          onClick={handleShow}
        />
      )}
      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({
              ...prev,
              active: false,
              archived: false,
            }))
            setLoading(true)
            setPagination({ current_page: 1 })
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Stations</h3>
            <span className="card_icon">
              <i className="icon_main chargin_stations"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({ ...prev, archived: false, active: true }))
            setLoading(true)
            setPagination({ current_page: 1 })
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Stations</h3>
            <span className="card_icon">
              <i className="icon_main activechecked"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.active_records || 0}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            !filterObj.active && filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({ ...prev, archived: true, active: false }))
            setLoading(true)
            setPagination({ current_page: 1 })
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Archive Stations</h3>
            <span className="card_icon">
              <i className="icon_main archive_data"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.archived_records || 0}</p>
        </div>
      </div>
      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <SearchBox
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e?.target?.value)
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                />
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>
      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <ChargingStationListSkeleton infiniteScroll={true} rows={1} />
              }
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">ID</th>
                    <th>Station Detail</th>
                    <th className="text-center">Consumption</th>
                    <th className="text-center">Bookings</th>
                    <th className="text-center">Revenue</th>
                    <th className="text-center">Available Ports</th>
                    <th className="text-center">QR Code</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data?.length > 0 &&
                    data?.map((item, index) => (
                      <tr key={index + 1}>
                        <td className="text-center">{item.id}</td>
                        <td>
                          <Link
                            to={`/charging-station-details/${item.id}`}
                            className="text-decoration-none"
                          >
                            <div className="station_nmaddress">
                              <p className="station_nm">{item.station_name}</p>
                              <span className="station_address">
                                {item.address}
                              </span>
                            </div>
                          </Link>
                        </td>
                        <td className="text-center">
                          {item?.total_consumption?.toFixed(2) ?? 0} W
                        </td>
                        <td className="text-center">
                          {item?.booking_count ?? 0}
                        </td>
                        <td className="text-center text-nowrap">
                          {item?.total_revenue?.toFixed(2) ?? 0} ₹
                        </td>
                        <td className="text-center">
                          <div className="available_ports">
                            <span className="available_nmerp">
                              {item.chargers.reduce((total, charger) => {
                                const availableConnectors =
                                  charger.connectors.filter(
                                    (connector) =>
                                      connector.status === "Available",
                                  ).length
                                return total + availableConnectors
                              }, 0)}
                            </span>
                            /
                            {item.chargers.reduce(
                              (total, charger) =>
                                total + charger.connectors.length,
                              0,
                            )}
                          </div>
                        </td>

                        <td className="text-center">
                          <button
                            className="btn_icon btn btn-btnrestore_icon"
                            onClick={() => {
                              setQrModel(item?.id)
                            }}
                          >
                            <i className="icon_main qrcode "></i>
                          </button>
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit &&
                                !filterObj.archived &&
                                item?.deletedAt === null && (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Edit"
                                  >
                                    <Button
                                      variant="btnedit_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        handleShow()
                                        setIsUpdate(item)
                                      }}
                                    >
                                      <i className="icon_main edit_pencil"></i>
                                    </Button>
                                  </TooltipComponent>
                                )}
                              {canDelete &&
                                (item?.deletedAt === null ? (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Archive"
                                  >
                                    <Button
                                      variant="btnarchive_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(false)
                                        setShowAlert(true)
                                        setIsDelete(item)
                                      }}
                                    >
                                      <i className="icon_main archive_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ) : (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Restore"
                                  >
                                    <Button
                                      variant="btnrestore_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(true)
                                        setShowAlert(true)
                                        setIsDelete(item)
                                      }}
                                    >
                                      <i className="icon_main restore_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ))}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data && !data?.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={6}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <ChargingStationListSkeleton />
      )}
      <AddEditChargingStation
        show={show}
        handleClose={handleClose}
        setShow={setShow}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        setData={setData}
        isUpdate={isUpdate}
      />

      <SweetAlerts
        showAlert={showAlert}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this Charging station?`}
        alertSubMessage={`${
          archived
            ? "Then please assign charger."
            : "Associated charger will be remove after Archive!"
        }`}
        handleClose={handleClose}
        iconClassName={`${
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }`}
        handleAlert={() => {
          handleArchive(isDelete.id)
        }}
      />
      <ModalComponent
        show={qrModel ? true : false}
        handleClose={closeQrModel}
        modalsize={"md"}
        modeltitle={`${qrModel}`}
        backdrop={"static"}
        animationFade={true}
        onHide={closeQrModel}
        children={
          <>
            <div className="text-center">
              <QRCode
                ref={qrCanvasRef}
                value={`https://booking.megacharge.co.in/login/${qrModel}`}
                size={360}
                bgColor={"#ffffff"}
                fgColor={"#000000"}
              />
            </div>

            <Button
              variant="primary"
              className="w-100"
              onClick={downloadQRCode}
            >
              Download QR Code
            </Button>
          </>
        }
        size="lg"
      />
    </>
  )
}

export default ChargingStations
